import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { authActions } from '../store/reducers/authReducer';

// Icons
import MonitorIcon from '@mui/icons-material/Monitor';
import ExploreIcon from '@mui/icons-material/Explore';
import PlaceIcon from '@mui/icons-material/Place';
import MapIcon from '@mui/icons-material/Map';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import QuizIcon from '@mui/icons-material/Quiz';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import FaceIcon from '@mui/icons-material/Face';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import StoreIcon from '@mui/icons-material/Store';
import CropFreeIcon from '@mui/icons-material/CropFree';

interface SidebarProps {
  collapsed: boolean;
}

const PRIMARY_PURPLE = '#6366f1';
const TEXT_MUTED = '#64748b';
const TEXT_DARK = '#1e293b';

const Sidebar = ({ collapsed }: SidebarProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user) as any;

  const handleLogout = () => {
    dispatch(authActions.logout());
    window.location.href = '/login';
  };

  const menuItems = [
    { text: 'Screens', icon: <MonitorIcon />, path: '/screen' },
    { text: 'Spaces', icon: <ExploreIcon />, path: '/spaces' },
    { text: 'Locations', icon: <PlaceIcon />, path: '/locations' },
    { text: 'Maps', icon: <MapIcon />, path: '/maps' },
    { text: 'Models', icon: <ViewInArIcon />, path: '/models' },
    { text: 'Feedback Questions', icon: <QuizIcon />, path: '/feedback-question' },
    { text: 'Labels', icon: <LabelIcon />, path: '/labels' },
    { text: 'Info Pages', icon: <InfoIcon />, path: '/pages' },
    { text: 'Selfie Masks', icon: <FaceIcon />, path: '/selfie-mask' },
    { text: 'Treasure Hunt', icon: <EmojiEventsIcon />, path: '/treasure' },
    // { text: 'Geo Fences', icon: <CropFreeIcon />, path: '/fences' },
    { text: 'Users', icon: <PeopleIcon />, path: '/user' },
  ];

  return (
    <Box sx={{
      width: collapsed ? 80 : 260,
      height: '100vh',
      bgcolor: 'white',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      borderRight: '1px solid #e2e8f0'
    }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: PRIMARY_PURPLE, width: 40, height: 40 }}>
          <StoreIcon />
        </Avatar>
        {!collapsed && (
          <Typography variant="h6" sx={{ fontWeight: 800, color: TEXT_DARK, letterSpacing: 1 }}>
            HERITAGE
          </Typography>
        )}
      </Box>

      {/* User Session Section */}
      <Box sx={{ px: 2, py: 3, display: 'flex', flexDirection: 'column', alignItems: collapsed ? 'center' : 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={user?.avatar}
            sx={{
              width: collapsed ? 45 : 55,
              height: collapsed ? 45 : 55,
              border: `2px solid #f1f5f9`,
              boxShadow: '0px 4px 12px rgba(0,0,0,0.05)'
            }}
          />
          {!collapsed && (
            <Box>
              <Typography sx={{ fontWeight: 700, color: TEXT_DARK, fontSize: '0.95rem' }}>
                {user ? `${user.first_name} ${user.last_name[0]}.` : 'Guest'}
              </Typography>
              <Typography sx={{ color: TEXT_MUTED, fontSize: '0.75rem', fontWeight: 500 }}>
                Administrator
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Navigation List */}
      <List sx={{ flex: 1, px: 0, py: 0 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  px: collapsed ? 2.2 : 3,
                  py: 1.5,
                  position: 'relative',
                  bgcolor: isActive ? '#eff6ff' : 'transparent',
                  color: isActive ? PRIMARY_PURPLE : TEXT_MUTED,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    color: PRIMARY_PURPLE
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: 'inherit',
                  minWidth: collapsed ? 0 : 35,
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 700 : 500
                    }}
                  />
                )}

                {/* Active Indicator */}
                {isActive && (
                  <Box sx={{
                    position: 'absolute',
                    right: 0,
                    top: '15%',
                    bottom: '15%',
                    width: 4,
                    bgcolor: PRIMARY_PURPLE,
                    borderRadius: '4px 0 0 4px'
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer / Logout Section */}
      <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            px: collapsed ? 2.2 : 3,
            color: TEXT_MUTED,
            '&:hover': { color: '#ef4444' }
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: collapsed ? 0 : 35 }}>
            <LogoutIcon />
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );
}

export default Sidebar;
